// Personal Information
export const PERSONAL_INFO = {
  name: 'Sibi Sebastian',
  initials: 'SS',
  email: 'sibisebastian.dev@gmail.com',
  title: 'Freelance Full-Stack Software Developer',
  description: "Full-stack developer based in Karnataka, India. Over the past 2+ years I've built APIs, databases, and interfaces that power real products — from production backends serving thousands of users to complete e-commerce platforms.",
  location: 'Karnataka, India',
  availability: 'Open to freelance projects — remote worldwide, on-site across India',
  status: 'Available for work'
};

// Navigation
export const NAV_LINKS = [
  { label: 'About', href: '#about' },
  { label: 'Experience', href: '#experience' },
  { label: 'Work', href: '#work' },
  { label: 'Blog', href: '#blog' },
  { label: 'Contact', href: '#contact' }
];

// Social Media Links
export const SOCIAL_LINKS = [
  {
    name: 'GitHub',
    url: 'https://github.com/Sibi-Sebastian',
    icon: '/assets/png/github-ico.png'
  },
  {
    name: 'LinkedIn',
    url: 'https://www.linkedin.com/in/sibi-sebastian-b71092220/',
    icon: '/assets/png/linkedin-ico.png'
  },
  {
    name: 'X',
    url: 'https://x.com/sibi_sebastian_',
    icon: '/assets/png/x.png'
  },
  {
    name: 'Instagram',
    url: 'https://www.instagram.com/sibi.sebastian_/',
    icon: '/assets/png/insta-ico.png'
  }
];

// Quick stats shown in About
export const STATS = [
  { value: '2+', label: 'Years of experience' },
  { value: '3', label: 'Products live in production' },
  { value: 'B.E.', label: 'AI & ML — VTU' }
];

// Skills, grouped by discipline
export const SKILL_GROUPS = [
  {
    title: 'Backend',
    skills: ['Node.js', 'Express.js', 'REST APIs', 'Microservices', 'Authentication']
  },
  {
    title: 'Frontend',
    skills: ['React.js', 'JavaScript', 'Responsive Design', 'SEO', 'Performance']
  },
  {
    title: 'Databases',
    skills: ['MongoDB', 'MySQL', 'SQL', 'Schema Design']
  },
  {
    title: 'Tools & Platforms',
    skills: ['Jest', 'Git', 'Firebase', 'PHP', 'WordPress', 'Hostinger']
  }
];

// Experience timeline
export const EXPERIENCE = [
  {
    id: 1,
    period: 'Now',
    role: 'Freelance Full-Stack Developer',
    company: 'Independent',
    points: [
      'Building web applications and APIs end to end for clients — from scoping and architecture to deployment and handover.',
      'Shipped client products including a furniture showcase platform and a complete e-commerce store with payments and order management.',
      'Own the full delivery pipeline: responsive frontends, backend services, databases, and hosting on Firebase and Hostinger.'
    ]
  },
  {
    id: 2,
    period: 'Previously',
    role: 'Backend Developer',
    company: 'Tackle-D Private Limited',
    points: [
      'Developed REST APIs with Node.js and Express powering production applications serving thousands of users.',
      'Designed and managed SQL databases, authentication flows, and integrations.',
      'Wrote unit and integration tests with Jest and helped keep production deployments reliable.'
    ]
  }
];

// About Me Content
export const ABOUT_ME = {
  intro: "I'm a full-stack developer from Karnataka, India, with a B.E. in Artificial Intelligence and Machine Learning from VTU. I got my start as a Backend Developer at Tackle-D Private Limited, building REST APIs and databases for applications serving thousands of users. These days I work freelance, taking products from idea to production with Node.js, React.js, and the MERN stack.",
  details: "I work across the whole stack — API design, data modeling, authentication, testing, deployment — but backend engineering is where I'm strongest. I care about code that's tested, maintainable, and fast, and about picking the right tool for the job rather than the trendiest one."
};

// Projects
export const PROJECTS = [
  {
    id: 1,
    title: 'Seyon Naturals',
    kind: 'E-commerce Platform',
    description: 'Complete online store for natural products — product catalog, cart, payment gateway integration, and order management. Built for a client who runs it day-to-day themselves.',
    image: '/assets/projects/seyon.png',
    link: 'https://seyonnaturals.com',
    technologies: ['WordPress', 'E-Commerce', 'Payments', 'Hostinger']
  },
  {
    id: 2,
    title: 'Adishakthi Furnitures',
    kind: 'Business Website',
    description: 'Furniture showcase platform with product galleries, category filtering, and contact flows. Tuned for smooth performance on low-end mobile devices.',
    image: '/assets/projects/adishakthi.png',
    link: 'https://adishakthifurnitures.com',
    technologies: ['PHP', 'JavaScript', 'CSS3']
  },
  {
    id: 3,
    title: 'Portfolio Website',
    kind: 'Personal Project',
    description: 'This site — a React single-page app with a content-driven architecture, Web3Forms contact integration, and tuned cache headers on Firebase Hosting.',
    image: '/assets/projects/portfolio.png',
    link: 'https://sibisebastian.web.app',
    technologies: ['React.js', 'JavaScript', 'Firebase']
  }
];

// Blog Posts
export const BLOG_POSTS = [
  {
    id: 1,
    title: 'How I Got Into Software Development',
    excerpt: 'From studying AI and machine learning at VTU to shipping production APIs as a backend developer — the path that got me here.',
    date: 'January 2025',
    author: 'Sibi Sebastian',
    tags: ['Career', 'Backend'],
    content: [
      'My path into software development started during my B.E. in Artificial Intelligence and Machine Learning at VTU. What began as curiosity about how applications work behind the scenes turned into a genuine passion for building backend systems.',
      'I discovered early on that I enjoyed the server side more than the browser. The challenge of designing scalable APIs and working with databases pulled me in far more than pixel-pushing ever did.',
      'Things really took off when I joined Tackle-D Private Limited as a Backend Developer. Working on real applications with real users forced me to level up fast — Node.js, Express, SQL databases, and all the operational discipline that comes with production systems.',
      'Today I specialize in REST APIs, microservices, and full-stack MERN applications, covering everything from database design to deployment, with a strong focus on testing and performance.',
      "The path is never linear, and that's fine. If you're starting out, my honest advice is simple: build real things, break them, fix them, and keep learning. Consistency beats talent that doesn't show up."
    ]
  },
  {
    id: 2,
    title: 'Three Projects, Three Different Stacks',
    excerpt: 'What I learned building a PHP showcase site, a WordPress store, and a React portfolio — and why the right tool depends on the job.',
    date: 'January 2025',
    author: 'Sibi Sebastian',
    tags: ['Projects', 'Web Development'],
    content: [
      "Every project I've shipped has taught me something different, mostly because each one used a different stack. Here's a quick tour of three of them and what each taught me.",
      "Adishakthi Furnitures is a furniture showcase site built with PHP. The interesting problems there were front-of-house: responsive product galleries, category filtering, and animations that stay smooth on cheap phones. It taught me that performance work matters most on the devices you don't own.",
      "Seyon Naturals is a full e-commerce store on WordPress — product catalog, cart, payment gateway integration, and order management. WordPress gets sneered at by developers, but for a small business that needs to manage its own products, it's often the pragmatic choice. Knowing when not to write custom code is a skill too.",
      'This portfolio is the third: a React app deployed on Firebase Hosting. Component-based architecture, a single data file driving all the content, and cache headers tuned so repeat visits are instant.',
      'The common thread: pick the stack that fits the client and the problem, not the one that looks best on a resume. Then apply the same standards everywhere — organized code, security basics, and a fast experience for the end user.'
    ]
  },
  {
    id: 3,
    title: 'Deploying a React App on Firebase Hosting',
    excerpt: 'A practical walkthrough of how this site is built and deployed — React, Firebase CLI, SPA rewrites, and cache headers.',
    date: 'January 2025',
    author: 'Sibi Sebastian',
    tags: ['Firebase', 'React', 'Deployment'],
    content: [
      "This site is a React app hosted on Firebase Hosting, and the whole deploy pipeline is two commands. Here's how it works and why I set it up this way.",
      'The app itself is deliberately simple: a handful of components (header, hero, projects, blog, contact) and one constants file that holds all the content. Updating the site means editing one data file — no digging through JSX.',
      "Firebase setup starts with `firebase init hosting`, pointing the public directory at the React build output. The one setting that matters for a single-page app is the rewrite rule sending every route to index.html, so client-side routing doesn't 404.",
      'Deploying is `npm run build` followed by `firebase deploy`. I wired both into a single npm script, so shipping a change is one command from a clean working tree.',
      'The detail most people skip is cache headers. I set static assets (JS, CSS, images) to cache for a year — their filenames are content-hashed, so they can never go stale — while index.html is never cached, so a new deploy is picked up immediately.',
      "The result is a fast, effectively free hosting setup with SSL, a CDN, and one-command deploys. For a static site or SPA, it's hard to beat."
    ]
  }
];

// Form Configuration
export const FORM_CONFIG = {
  accessKey: '9ed4feeb-f492-4a5d-9905-5031f53f9887',
  apiUrl: 'https://api.web3forms.com/submit',
  subject: 'New Contact Form Submission from Portfolio',
  fromName: 'Portfolio Contact Form'
};
